import { getSupabase } from '../lib/supabase';
import { formatApiError } from '../lib/apiError';
import { assertOnlineOrThrow } from '../lib/offlineGuard';

function sb() {
  const client = getSupabase();
  if (!client) throw new Error('SUPABASE_MISSING');
  return client;
}

/** @typedef {'none'|'pending_sent'|'pending_received'|'accepted'|'blocked'} FriendshipStatus */

/**
 * @param {string} query
 * @param {number} [limit]
 * @returns {Promise<Array<{ user_id: string, nickname: string, handle: string|null, avatar_url: string|null, region: string, friendship_status: FriendshipStatus, friendship_id: string|null }>>}
 */
export async function searchUsers(query, limit = 20) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('search_users', {
    p_query: query || '',
    p_limit: limit,
  });
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}

/**
 * @param {unknown} error
 * @returns {'SELF'|'BLOCKED'|'NOT_FOUND'|'GENERIC'}
 */
export function mapFriendRequestError(error) {
  const msg = String(error?.message || formatApiError(error) || '');
  if (/self_friend_request/i.test(msg)) return 'SELF';
  if (/chat_blocked|friendship_blocked/i.test(msg)) return 'BLOCKED';
  if (/user_not_found/i.test(msg)) return 'NOT_FOUND';
  return 'GENERIC';
}

/** @returns {Promise<string>} friendship_id */
export async function sendFriendRequest(addresseeUserId) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('send_friend_request', {
    p_addressee_id: addresseeUserId,
  });
  if (error) {
    const code = mapFriendRequestError(error);
    const e = new Error(code);
    e.code = code;
    throw e;
  }
  return data;
}

/** @param {string} friendshipId */
export async function acceptFriendRequest(friendshipId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('accept_friend_request', {
    p_friendship_id: friendshipId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** @param {string} friendshipId */
export async function declineFriendRequest(friendshipId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('decline_friend_request', {
    p_friendship_id: friendshipId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** @param {string} friendshipId */
export async function cancelFriendRequest(friendshipId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('cancel_friend_request', {
    p_friendship_id: friendshipId,
  });
  if (error) throw new Error(formatApiError(error));
}

/**
 * @returns {Promise<Array<{ friendship_id: string, user_id: string, nickname: string, avatar_url: string|null, friends_since: string, is_mingo?: boolean }>>}
 */
export async function getMyFriends() {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('get_my_friends');
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}

/** 친구 삭제 (accepted → 관계 해제). @param {string} friendshipId */
export async function removeFriend(friendshipId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('remove_friend', {
    p_friendship_id: friendshipId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** 친구 차단 (관계 + 채팅/피드 차단). @param {string} otherUserId */
export async function blockFriend(otherUserId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('block_friend', {
    p_other_user_id: otherUserId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** 친구 차단 해제. @param {string} otherUserId */
export async function unblockFriend(otherUserId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('unblock_friend', {
    p_other_user_id: otherUserId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** 친구 숨김 (내 목록에서만). @param {string} otherUserId */
export async function hideFriend(otherUserId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('hide_friend', {
    p_other_user_id: otherUserId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** 친구 숨김 해제. @param {string} otherUserId */
export async function unhideFriend(otherUserId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('unhide_friend', {
    p_other_user_id: otherUserId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** Mingo(친한 친구) 지정. @param {string} otherUserId */
export async function setMingoFriend(otherUserId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('set_mingo_friend', {
    p_other_user_id: otherUserId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** Mingo(친한 친구) 해제. @param {string} otherUserId */
export async function unsetMingoFriend(otherUserId) {
  assertOnlineOrThrow();
  const { error } = await sb().rpc('unset_mingo_friend', {
    p_other_user_id: otherUserId,
  });
  if (error) throw new Error(formatApiError(error));
}

/** Authors who marked the current user as 찐친 (mingo_friends audience). */
export async function getMingoAudienceAuthorIds() {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('get_mingo_audience_author_ids');
  if (error) throw new Error(formatApiError(error));
  return new Set((data || []).map((row) => row?.author_user_id).filter(Boolean));
}

/**
 * @returns {Promise<Array<{ friendship_id: string, user_id: string, nickname: string, avatar_url: string|null, friends_since: string }>>}
 */
export async function getHiddenFriends() {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('get_hidden_friends');
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}

/**
 * @param {number} [limit]
 * @param {string|null} [cursorAt]
 * @param {string|null} [cursorId]
 */
export async function getReceivedFriendRequests(limit = 20, cursorAt = null, cursorId = null) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('get_received_friend_requests', {
    p_limit: limit,
    p_cursor_at: cursorAt,
    p_cursor_id: cursorId,
  });
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}

/**
 * @param {number} [limit]
 * @param {string|null} [cursorAt]
 * @param {string|null} [cursorId]
 */
export async function getSentFriendRequests(limit = 20, cursorAt = null, cursorId = null) {
  assertOnlineOrThrow();
  const { data, error } = await sb().rpc('get_sent_friend_requests', {
    p_limit: limit,
    p_cursor_at: cursorAt,
    p_cursor_id: cursorId,
  });
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}
